import apiClient, { type AxiosResponse } from "@shared/services/apiClient";

export interface EEGRecordingSummary {
  recording_id: string;
  filename: string;
  processing_status: string;
  archival_status?: string | null;
  derivation_type?: string | null;
  derivation_metadata?: Record<string, unknown> | null;
  merged_source_count?: number;
  is_primary?: boolean;
  sampling_rate?: number;
  num_channels?: number;
  duration_seconds?: number;
  logical_duration_seconds?: number;
  segment_count?: number;
  start_datetime?: string;
  recording_id_from_edf?: string;
  patient_id_from_edf?: string;
  created_at?: string;
  updated_at?: string;
}

export interface EEGStudyRecordingsMeta {
  total_count: number;
  total_duration_seconds: number;
  returned_count: number;
  has_more: boolean;
}

export interface EEGStudySummary {
  eeg_study_id: string;
  study_date: string;
  status: string;
  priority: string;
  recordings?: EEGRecordingSummary[];
  recordings_meta?: EEGStudyRecordingsMeta;
  eeg_type?: any;
}

export interface StudyUiPolicyContract {
  columns: Array<{
    key: string;
    visible: boolean;
    reasonCode?: string | null;
  }>;
  datetime: {
    showDate: boolean;
    showTimeMode: string;
    hideMidnightDefault: boolean;
    tooltipFormat: string;
  };
  pagination: {
    defaultPageSize: number;
    maxPageSize: number;
    allowedSizes: number[];
  };
}

export type StudyRecordingsMode = "full" | "inline_limited" | "none";

export interface GetStudiesForPatientOptions {
  recordingsMode?: StudyRecordingsMode;
  recordingsLimit?: number;
  /** When true, include studies with zero active EEG recordings (awaiting upload). Default false. */
  includeEmpty?: boolean;
}

export interface GetStudiesForPatientResponse {
  data?:
    | {
        studies?: EEGStudySummary[];
        uiPolicy?: StudyUiPolicyContract;
        [key: string]: unknown;
      }
    | EEGStudySummary[];
  studies?: EEGStudySummary[];
  uiPolicy?: StudyUiPolicyContract;
}

export interface GetStudyRecordingsQuery {
  limit?: number;
  cursor?: string | null;
  q?: string;
  status?: string[];
}

export interface StudyIndexItem {
  id: string;
  recordingId: string;
  studyId: string;
  patientId: string | null;
  patientName: string;
  patientMRN: string;
  studyType: string;
  studyDate: string | null;
  duration: string;
  status: string;
  priority: string;
  technologist: string;
  filename?: string;
  archiveStatus?: string | null;
  archivalHold?: boolean;
  archiveDueAt?: string | null;
  activeWindowExpiresAt?: string | null;
}

export interface StudyIndexQuery {
  limit?: number;
  cursor?: string | null;
  q?: string;
  processing?: string;
  status?: string;
  studyStatus?: string;
  studyScope?: string;
  assigned?: string;
  ownership?: string;
  priority?: string;
  range?: string;
  aiTriage?: string;
  monitoring?: string;
  /** study (default) | recording — clinical vs pipeline triage grain */
  grain?: "study" | "recording";
  /** Include studies with zero active recordings (default false) */
  includeEmpty?: boolean;
}

export interface StudyIndexResponse {
  items: StudyIndexItem[];
  pageInfo: {
    limit: number;
    hasNextPage: boolean;
    nextCursor: string | null;
    totalFiltered: number;
    totalOverall: number;
    countMode?: "study" | "recording";
  };
  filters: {
    q: string | null;
    processing: string;
    status: string[];
    availableStatuses: string[];
    aiTriage?: string | null;
    monitoring?: string | null;
  };
  scope: {
    organizationId: string;
  };
}

export interface RecordingSearchItem {
  recordingId: string;
  studyId: string;
  patientId: string | null;
  patientName: string;
  mrn: string;
  examId: string;
  studyDate: string | null;
  processingStatus: string | null;
  filename: string | null;
}

export interface RecordingSearchQuery {
  q?: string;
  limit?: number;
  cursor?: string | null;
  processing?: "ready" | "all";
}

export interface RecordingSearchResponse {
  items: RecordingSearchItem[];
  pageInfo: {
    limit: number;
    hasNextPage: boolean;
    nextCursor: string | null;
    totalFiltered: number;
  };
  scope: {
    organizationId: string;
  };
}

export interface StudyRecordingsPageResponse {
  studyId: string;
  items: EEGRecordingSummary[];
  pageInfo: {
    limit: number;
    hasNextPage: boolean;
    nextCursor: string | null;
    totalFiltered: number;
    totalOverall: number;
  };
  filters: {
    q: string | null;
    status: string[];
    availableStatuses?: string[];
  };
}

export interface StudyFolderFileItem {
  id: string;
  file_type: "eeg" | "video";
  file_name: string;
  recording_id?: string | null;
  exam_id?: string | null;
  relative_path?: string | null;
  is_segment?: boolean;
  segment_id?: string | null;
  segment_index?: number | null;
  segment_start_datetime?: string | null;
  segment_end_datetime?: string | null;
  duration_seconds?: number | null;
  modified_at: string;
  modified_source?: "filesystem" | "database";
  storage_provider?: string;
}

export interface StudyFolderFilesResponse {
  studyId: string;
  studyDate?: string;
  items: StudyFolderFileItem[];
  summary?: {
    eeg_count: number;
    video_count: number;
    total_count: number;
  };
}

export interface StudyRecordingMergePreflightResponse {
  studyId: string;
  eligible: boolean;
  workflowStatusCode: string;
  policyDeniedReason: string | null;
  blockingReasons: Array<{
    code: string;
    message: string;
    recordingIds?: string[];
  }>;
  timelineConflicts: Array<{
    leftRecordingId: string;
    rightRecordingId: string;
    leftEndIso: string;
    rightStartIso: string;
    overlapMs: number;
  }>;
  orderedRecordings: Array<{
    recordingId: string;
    filename: string;
    examId: string;
    startDatetime: string;
    endDatetime: string;
    durationSeconds: number;
    samplingRate: number;
    numChannels: number;
    formatCode: string;
    processingStatus: string;
  }>;
  projection: {
    startDatetime: string | null;
    endDatetime: string | null;
    durationSeconds: number;
    sourceDurationSeconds: number;
    segmentCount: number;
  };
}

export interface StudyRecordingMergeExecuteResponse {
  mergeJobId: string;
  mergedRecordingId: string;
  status: "queued" | "running" | "completed" | "failed";
  preflight: StudyRecordingMergePreflightResponse;
}

export interface StudyRecordingMergeJobStatusResponse {
  mergeJobId: string;
  studyId: string;
  mergedRecordingId: string | null;
  status: string;
  statusMessage: string | null;
  idempotencyKey: string;
  requestedBy: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  errorJson: unknown;
  sourceRecordings: Array<{
    recordingId: string;
    sequenceIndex: number;
    startDatetime: string;
    endDatetime: string;
    durationSeconds: number;
  }>;
}

const prefix = "/patients";

export function resolveStudiesForPatientPayload(payload: unknown): {
  studies: EEGStudySummary[];
  uiPolicy: StudyUiPolicyContract | null;
} {
  const candidate = payload as GetStudiesForPatientResponse | null | undefined;
  const nestedData =
    candidate?.data &&
    typeof candidate.data === "object" &&
    !Array.isArray(candidate.data)
      ? candidate.data
      : null;
  const rows =
    candidate?.data && Array.isArray(candidate.data)
      ? candidate.data
      : nestedData
        ? nestedData.studies
        : candidate?.studies;
  const uiPolicy = nestedData?.uiPolicy ?? candidate?.uiPolicy;

  return {
    studies: Array.isArray(rows) ? rows : [],
    uiPolicy:
      uiPolicy && typeof uiPolicy === "object"
        ? (uiPolicy as StudyUiPolicyContract)
        : null,
  };
}

const getStudiesForPatient = async (
  patientId: string,
  options: GetStudiesForPatientOptions = {},
): Promise<AxiosResponse<GetStudiesForPatientResponse>> =>
  apiClient.get(`${prefix}/${patientId}/studies`, {
    params: {
      recordingsMode: options.recordingsMode,
      recordingsLimit: options.recordingsLimit,
      ...(options.includeEmpty === true ? { includeEmpty: "true" } : {}),
    },
  });

const getStudyRecordingsForPatient = async (
  patientId: string,
  studyId: string,
  query: GetStudyRecordingsQuery = {},
): Promise<AxiosResponse<{ data: StudyRecordingsPageResponse }>> => {
  const params: Record<string, string | number> = {};
  if (typeof query.limit === "number") params.limit = query.limit;
  if (query.cursor) params.cursor = query.cursor;
  if (typeof query.q === "string" && query.q.trim()) {
    params.q = query.q.trim();
  }
  if (query.status && query.status.length > 0) {
    params.status = query.status.join(",");
  }

  return apiClient.get(`${prefix}/${patientId}/studies/${studyId}/recordings`, {
    params,
  });
};

const getStudyFilesForPatient = async (
  patientId: string,
  studyId: string,
  recordingId?: string,
): Promise<AxiosResponse<{ data: StudyFolderFilesResponse }>> =>
  apiClient.get(`${prefix}/${patientId}/studies/${studyId}/files`, {
    params: recordingId ? { recording_id: recordingId } : undefined,
  });

const getRecentStudies = async (
  limit: number = 5,
): Promise<AxiosResponse<{ studies: EEGStudySummary[] }>> =>
  apiClient.get(`/eeg/recent-studies?limit=${limit}`);

const getStudyIndex = async (
  query: StudyIndexQuery = {},
): Promise<AxiosResponse<{ data: StudyIndexResponse }>> => {
  const params: Record<string, string | number> = {};
  if (typeof query.limit === "number") params.limit = query.limit;
  if (query.cursor) params.cursor = query.cursor;
  if (typeof query.q === "string" && query.q.trim()) {
    params.q = query.q.trim();
  }
  if (typeof query.processing === "string" && query.processing.trim()) {
    params.processing = query.processing.trim();
  }
  if (typeof query.status === "string" && query.status.trim()) {
    params.status = query.status.trim();
  }
  if (typeof query.studyStatus === "string" && query.studyStatus.trim()) {
    params.studyStatus = query.studyStatus.trim();
  }
  if (typeof query.studyScope === "string" && query.studyScope.trim()) {
    params.studyScope = query.studyScope.trim();
  }
  if (typeof query.assigned === "string" && query.assigned.trim()) {
    params.assigned = query.assigned.trim();
  }
  if (typeof query.ownership === "string" && query.ownership.trim()) {
    params.ownership = query.ownership.trim();
  }
  if (typeof query.priority === "string" && query.priority.trim()) {
    params.priority = query.priority.trim();
  }
  if (typeof query.range === "string" && query.range.trim()) {
    params.range = query.range.trim();
  }
  if (typeof query.aiTriage === "string" && query.aiTriage.trim()) {
    params.aiTriage = query.aiTriage.trim();
  }
  if (typeof query.monitoring === "string" && query.monitoring.trim()) {
    params.monitoring = query.monitoring.trim();
  }
  if (query.grain === "study" || query.grain === "recording") {
    params.grain = query.grain;
  }
  if (query.includeEmpty === true) {
    params.includeEmpty = "true";
  }

  return apiClient.get("/eeg/study-index", { params });
};

const searchRecordings = async (
  query: RecordingSearchQuery = {},
): Promise<AxiosResponse<{ data: RecordingSearchResponse }>> => {
  const params: Record<string, string | number> = {};
  if (typeof query.limit === "number") params.limit = query.limit;
  if (query.cursor) params.cursor = query.cursor;
  if (typeof query.q === "string" && query.q.trim()) {
    params.q = query.q.trim();
  }
  if (query.processing) params.processing = query.processing;

  return apiClient.get("/eeg/recording-search", { params });
};

const getStudyById = async (studyId: string): Promise<AxiosResponse<any>> =>
  apiClient.get(`/eeg/study/${studyId}`);

const duplicateRecording = async (
  studyId: string,
  recordingId: string,
  payload?: {
    target_study_id?: string;
    duplicate_mode?: string;
    include_annotations?: boolean;
    include_video_links?: boolean;
  },
): Promise<AxiosResponse<any>> =>
  apiClient.post(
    `/eegstudy/${studyId}/recordings/${recordingId}/duplicate`,
    payload || {},
  );

const preflightStudyRecordingMerge = async (
  studyId: string,
  recordingIds: string[],
): Promise<AxiosResponse<{ data: StudyRecordingMergePreflightResponse }>> =>
  apiClient.post(`/eegstudy/${studyId}/recordings/merge-preflight`, {
    recording_ids: recordingIds,
  });

const executeStudyRecordingMerge = async (
  studyId: string,
  payload: {
    recordingIds: string[];
    idempotencyKey: string;
  },
): Promise<AxiosResponse<{ data: StudyRecordingMergeExecuteResponse }>> =>
  apiClient.post(`/eegstudy/${studyId}/recordings/merge`, {
    recording_ids: payload.recordingIds,
    idempotency_key: payload.idempotencyKey,
  });

const getStudyRecordingMergeJobStatus = async (
  studyId: string,
  mergeJobId: string,
): Promise<AxiosResponse<{ data: StudyRecordingMergeJobStatusResponse }>> =>
  apiClient.get(`/eegstudy/${studyId}/recordings/merge-jobs/${mergeJobId}`);

export default {
  getStudiesForPatient,
  getStudyRecordingsForPatient,
  getStudyFilesForPatient,
  getStudyById,
  getRecentStudies,
  getStudyIndex,
  searchRecordings,
  duplicateRecording,
  preflightStudyRecordingMerge,
  executeStudyRecordingMerge,
  getStudyRecordingMergeJobStatus,
};
